import React from 'react'
import _ from 'lodash'
import SurveyResults from "@/routes/survey/SurveyResults"

const SENTIMENT_TABS = [
    { key: 'TOTAL',    label: 'Total',    filterFn: () => true },
    { key: 'POSITIVE', label: 'Positive', filterFn: a => a.sentiment === 'POSITIVE' },
    { key: 'NEUTRAL',  label: 'Neutral',  filterFn: a => a.sentiment === 'NEUTRAL' },
    { key: 'NEGATIVE', label: 'Negative', filterFn: a => a.sentiment === 'NEGATIVE' },
    { key: 'MIXED',    label: 'Mixed',    filterFn: a => a.sentiment === 'MIXED' },
    { key: 'NONE',     label: 'None',     filterFn: a => a.sentiment === 'NONE'     },
    { key: 'OTHER',    label: 'Other',    filterFn: a => a.sentiment === 'OTHER'    },
];

const SENTIMENT_CLASS_MAP = {
    POSITIVE: 'positive',
    MIXED:    'mixed',
    NEGATIVE: 'negative',
    NEUTRAL:  'neutral',
    NONE:     'none',
    OTHER:    'other',
};

const INITIAL_LOAD = 20;
const LOAD_MORE    = 20;

function SatisfactionPersonnelFeedbackSectionV2({ surveys, onModalClose }) {
    const [activeTab, setActiveTab] = React.useState('POSITIVE');
    const [visibleCount, setVisibleCount] = React.useState(INITIAL_LOAD);
    const loaderRef = React.useRef(null);
    const tabRefs = React.useRef({});
    const focusedTabKey = React.useRef('POSITIVE');

    // Flatten all answers that have a sentiment value
    const allAnswers = React.useMemo(() => {
        return _.flatMap(surveys, survey =>
            _.filter(survey.answers, a => a.sentiment)
                .map(a => ({ ...a, _surveyId: survey.id }))
        );
    }, [surveys]);

    const totalByKey = React.useMemo(() => {
        const counts = {};
        SENTIMENT_TABS.forEach(tab => {
            counts[tab.key] = tab.key === 'TOTAL'
                ? allAnswers.length
                : allAnswers.filter(tab.filterFn).length;
        });
        return counts;
    }, [allAnswers]);

    const total = allAnswers.length;

    const activeTabDef = SENTIMENT_TABS.find(t => t.key === activeTab);
    const filtered = React.useMemo(() => {
        const base = activeTabDef ? allAnswers.filter(activeTabDef.filterFn) : allAnswers;
        const noteworthy = _.orderBy(base.filter(a => a.noteworthy),  ['id'], ['desc']);
        const rest       = _.orderBy(base.filter(a => !a.noteworthy), ['id'], ['desc']);
        return [...noteworthy, ...rest];
    }, [allAnswers, activeTabDef]);

    React.useEffect(() => {
        setVisibleCount(INITIAL_LOAD);
    }, [activeTab]);

    React.useEffect(() => {
        if (!loaderRef.current) return;
        const observer = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && visibleCount < filtered.length) {
                setVisibleCount(c => c + LOAD_MORE);
            }
        }, { threshold: 0.1 });
        observer.observe(loaderRef.current);
        return () => observer.disconnect();
    }, [visibleCount, filtered.length]);

    const visible = filtered.slice(0, visibleCount);

    const getSurveyForAnswer = (answer) =>
        surveys.find(s => s.id === answer._surveyId);

    const moveFocus = (targetKey) => {
        // Update tabIndex via DOM directly — no React re-render, no NVDA double-read
        Object.entries(tabRefs.current).forEach(([key, el]) => {
            if (el) el.tabIndex = key === targetKey ? 0 : -1;
        });
        focusedTabKey.current = targetKey;
        tabRefs.current[targetKey]?.focus();
    };

    const handleTabKeyDown = (e, tabKey) => {
        const keys = SENTIMENT_TABS.map(t => t.key);
        const idx  = keys.indexOf(tabKey);

        if (e.key === 'ArrowRight') {
            e.preventDefault();
            moveFocus(keys[(idx + 1) % keys.length]);
        } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            moveFocus(keys[(idx - 1 + keys.length) % keys.length]);
        } else if (e.key === 'Home') {
            e.preventDefault();
            moveFocus(keys[0]);
        } else if (e.key === 'End') {
            e.preventDefault();
            moveFocus(keys[keys.length - 1]);
        } else if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setActiveTab(tabKey);
        }
    };

    const handleTabClick = (tabKey) => {
        focusedTabKey.current = tabKey;
        setActiveTab(tabKey);
    };

    const handleTablistBlur = (e) => {
        // When focus leaves the tablist entirely, reset roving tabIndex to the active tab
        // so Shift+Tab from the panel always returns to the selected tab.
        if (!e.currentTarget.contains(e.relatedTarget)) {
            Object.entries(tabRefs.current).forEach(([key, el]) => {
                if (el) el.tabIndex = key === activeTab ? 0 : -1;
            });
            focusedTabKey.current = activeTab;
        }
    };


    return (
        <div className="feedbackSectionV2">
            {/* Stat tabs — roving tabindex */}
            <div
                className="feedbackStatTabs"
                role="tablist"
                aria-label="Feedback by sentiment"
                onBlur={handleTablistBlur}
            >
                {SENTIMENT_TABS.map(tab => {
                    const count    = totalByKey[tab.key];
                    const pct      = tab.key !== 'TOTAL' && total > 0
                        ? Math.round((count / total) * 100)
                        : null;
                    const isActive  = activeTab === tab.key;
                    const pctText   = pct !== null ? `, ${pct}%` : '';
                    return (
                        <button
                            key={tab.key}
                            ref={el => { tabRefs.current[tab.key] = el; }}
                            role="tab"
                            aria-selected={isActive}
                            aria-controls="feedbackTabPanel"
                            id={`feedbackTab-${tab.key}`}
                            tabIndex={tab.key === activeTab ? 0 : -1}
                            className={`feedbackStatTab ${isActive ? 'active' : ''} sentiment-${tab.key.toLowerCase()}`}
                            onClick={() => handleTabClick(tab.key)}
                            onKeyDown={e => handleTabKeyDown(e, tab.key)}
                            aria-label={`${tab.label}, ${count}${pctText}`}
                        >
                            <div className="statTabCount" aria-hidden="true">{count}</div>
                            <div className="statTabLabel" aria-hidden="true">
                                {tab.label}
                                {pct !== null && <span className="statTabPct">{pct}%</span>}
                            </div>
                        </button>
                    );
                })}
            </div>

            {/* Tab panel */}
            <div
                id="feedbackTabPanel"
                role="tabpanel"
                aria-label={`${SENTIMENT_TABS.find(t => t.key === activeTab)?.label} feedback`}
                tabIndex={-1}
            >


                {visible.length === 0 ? (
                    <div className="feedbackEmptyState" role="status">
                        No {activeTab === 'TOTAL' ? '' : activeTabDef?.label.toLowerCase() + ' '}feedback available.
                    </div>
                ) : (
                    <div className="feedbackCardsGrid">
                        {visible.map(answer => {
                            const survey       = getSurveyForAnswer(answer);
                            const sentimentClass = SENTIMENT_CLASS_MAP[answer.sentiment] || 'other';
                            const noteworthyText = answer.noteworthy ? 'Noteworthy. ' : '';
                            const cardLabel    = `${noteworthyText}${answer.sentiment} feedback: ${answer.commentAnswer}`;
                            return (
                                <SurveyResults
                                    key={answer.id}
                                    surveyInstanceId={survey?.id}
                                    onModalClose={onModalClose}
                                >
                                    <div
                                        className={`feedbackCardV2 ${sentimentClass}`}
                                        role="button"
                                        aria-label={cardLabel}
                                        tabIndex={0}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                e.currentTarget.click();
                                            }
                                        }}
                                    >
                                        {answer.noteworthy && (
                                            <div className="noteworthyBadge" aria-hidden="true">
                                                <span className="noteworthyStar">&#9733;</span>
                                                <span>Noteworthy</span>
                                            </div>
                                        )}
                                        <div className="feedbackCardText" aria-hidden="true">{answer.commentAnswer}</div>
                                    </div>
                                </SurveyResults>
                            );
                        })}
                    </div>
                )}

                {visibleCount < filtered.length && (
                    <div ref={loaderRef} className="feedbackLoadMore" aria-hidden="true" />
                )}
            </div>
        </div>
    );
}

export default SatisfactionPersonnelFeedbackSectionV2;
